import { useQueries, useQueryClient } from '@tanstack/react-query';
import { loadFailureMessage } from '@evogard/domain';
import { api } from '../../../lib/api.js';
import { useSessionContext } from '../../../lib/session.js';
import type { DeskTenant, TenantSummary } from './deskBoard.js';

/** A raiz das chaves da Mesa — toda mudança invalida tudo daqui para baixo. */
export const DESK_KEY = ['cobranca', 'mesa-quadro'] as const;

export interface DeskFilters {
  min?: string;
  max?: string;
  paidFrom?: string;
  paidTo?: string;
}

/**
 * O resumo de cada base em que a pessoa tem vínculo.
 *
 * Uma chamada por base, porque a API responde uma base por vez. A base que
 * recusa (403: a pessoa não tem `billing.desk:ver` nela) fica fora do quadro, e
 * a tela diz qual — sem isso, um operador que só vê a CE acharia que a GO está
 * vazia.
 */
export function useDeskSummaries(filters: DeskFilters) {
  const { data: context } = useSessionContext();
  const tenants: DeskTenant[] = (context?.tenants ?? []).map((t) => ({
    id: t.id,
    slug: t.slug,
    name: t.nome,
  }));

  const results = useQueries({
    queries: tenants.map((tenant) => ({
      queryKey: [...DESK_KEY, 'resumo', tenant.slug, filters],
      queryFn: async () => {
        const r = await api.billingDesk.summary({ params: { tenant: tenant.slug }, query: filters });
        if (r.status === 403 || r.status === 404) return null;
        if (r.status !== 200) throw new Error(loadFailureMessage(r.status, `a Mesa da ${tenant.name}`));
        return r.body;
      },
      refetchInterval: 60_000,
    })),
  });

  const ready: TenantSummary[] = [];
  const refused: DeskTenant[] = [];
  const errors: string[] = [];
  results.forEach((r, i) => {
    const tenant = tenants[i]!;
    if (r.error) errors.push(r.error.message);
    else if (r.data === null) refused.push(tenant);
    else if (r.data) ready.push({ tenant, summary: r.data });
  });

  return {
    context,
    tenants,
    ready,
    refused,
    errors,
    loading: results.some((r) => r.isLoading),
  };
}

export function useInvalidateDesk() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: DESK_KEY });
}
